import { initReactI18next } from 'react-i18next'
import { Provider } from 'react-redux'
import { LastLocationProvider } from 'react-router-last-location'

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { ConnectedRouter } from 'connected-react-router'
import i18n, { type InitOptions } from 'i18next'

import { ThemeProvider } from 'antd-style'

import httpClient from '@emmacare/common/api/httpClient'
import telemedicine from '@emmacare/common/i18n/locales/en/telemedicine.json'
import { ANT_THEME_PREFIX } from '@emmacare/common/theme/config'
import type { ICustomToken } from '@emmacare/common/theme/customToken.types'
import GlobalStyle from '@emmacare/common/theme/default/GlobalStyle'
import customToken from '@emmacare/common/theme/default/customToken.json'
import defaultTheme from '@emmacare/common/theme/default/default.theme.json'

import { logoutUser, refreshUser } from 'src/store/modules/auth'

import 'src/assets/styles/main.pcss'
import { history, store } from 'src/store'
import { API_BASE_URL, DEFAULT_HEADERS } from 'src/utils/constants'

import Root from './components/Root'

declare module 'antd-style' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  export interface CustomToken extends ICustomToken {}
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 0,
      gcTime: 0,
      staleTime: 0,
      retryOnMount: false
      // suspense: true
    }
  }
})

httpClient.initialize({
  store,
  onLogout: logoutUser,
  onRefreshToken: refreshUser,
  onNoInternetConnection: null,
  API_URL: API_BASE_URL,
  API_HEADERS: DEFAULT_HEADERS
})

// draft, don't use
i18n.use(initReactI18next).init({
  initAsync: false,
  fallbackLng: 'en',
  lng: 'en',
  preload: ['en'],
  resources: {
    en: {
      telemedicine
    }
  },
  react: {
    useSuspense: false
  },
  interpolation: {
    escapeValue: false
  }
} as InitOptions)

const App = ({ maintenance = false }: { maintenance: boolean }) => (
  <ThemeProvider<ICustomToken>
    prefixCls={ANT_THEME_PREFIX}
    customToken={customToken}
    theme={defaultTheme}>
    <GlobalStyle />
    <QueryClientProvider client={queryClient}>
      <Provider store={store}>
        <ConnectedRouter history={history}>
          {/* <React.StrictMode> */}
          <LastLocationProvider>
            <Root maintenance={maintenance} />
          </LastLocationProvider>
          {/* </React.StrictMode> */}
        </ConnectedRouter>
      </Provider>
      <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
    </QueryClientProvider>
  </ThemeProvider>
)

export default App
